// components/DownloadMtExcelComponent.tsx
import { DownloadIcon } from "@chakra-ui/icons";
import {
  IconButton,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { AppContext } from "AppProvider/AppContext";
import { useContext, useState } from "react";
import { appRequest } from "routes";

const DownloadMtExcelComponent = ({ type, searchValue, selectedOptions, tabName, statusType, dateFilterKey, transportType, processName: propProcessName, processType: propProcessType, ...props }: any) => {
  const { workflowProcess, accountDetails, startDate, endDate, workFlowData } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const downloadExcel = async () => {
    const processName = propProcessName || workflowProcess?.processName || workFlowData?.processName;
    const processType = propProcessType || workflowProcess?.processType || workFlowData?.processType;

    if (!processName) {
      toast({
        title: "Error",
        description: "Workflow process not selected",
        status: "error",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setIsLoading(true);
    try {
      // Transforming selectedOptions into searchFilters for the backend
      let searchFilters: any[] = [];
      if (selectedOptions && typeof selectedOptions === 'object') {
        Object.entries(selectedOptions).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            searchFilters.push({ [key]: value });
          } else if (value && !Array.isArray(value)) {
            searchFilters.push({ [key]: value });
          }
        });
      }

      const response: any = await appRequest("mmtDocument", "downloadExcel", {
        level: accountDetails.level,
        status: type,
        processName: processName,
        processType: processType,
        workflowId: workFlowData?._id,
        activeWorkflow: workFlowData?._id,
        _id: accountDetails.user,
        dateFrom: startDate,
        dateTo: endDate,
        dateFilterKey: dateFilterKey || workFlowData?.dateFilter?.filterKey || "created_At",
        value: searchValue || "", // Search text
        searchFilters: searchFilters,
        statusType: statusType,
        transportType: transportType || "",
        tabName: tabName
      });

      const base64String = response?.base64 || response;
      if (!base64String || typeof base64String !== "string") {
        throw new Error("Invalid response: no Excel data");
      }

      const base64 = base64String.includes(",")
        ? base64String.split(",")[1]
        : base64String;

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `MT_Invoices_${tabName || type}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "MT Excel downloaded successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Download failed:", error);
      toast({
        title: "Download Failed",
        description: error.message || "Please try again",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Tooltip label={`Download`} hasArrow>
      <IconButton
        onClick={downloadExcel}
        aria-label={`Download ${tabName || type} Excel`}
        title={`Download ${tabName || type} Excel`}
        icon={<DownloadIcon />}
        colorScheme="blue"
        variant="solid"
        size="sm"
        isLoading={isLoading}
        isDisabled={isLoading}
        {...props}
      />
    </Tooltip>
  );
};

export default DownloadMtExcelComponent;
